<template>
  <v-dialog
    v-model="visible"
    content-class="custom-dialog"
    max-width="440"
    scrollable
  >
    <v-card tag="form">
      <v-card-title>
        <app-logo />
        <span>Race Settings</span>
        <v-spacer />
        <v-btn
          color="grey darken-1"
          icon
          small
          @click="close"
        >
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <loader v-if="loading && !isRaceInfo" class="ma-16" />
      <v-card-text v-else>
        <v-text-field
          v-model="form.event"
          dense
          hide-details
          label="Display Name"
          outlined
        />
        <base-date-picker
          :value="form.date || ''"
          @change="v => form.date = v"
        />
        <div class="d-flex justify-space-between">
          <v-switch
            v-model="form.active"
            dense
            hide-details
            inset
            label="Active Race"
            :ripple="false"
          />
          <div class="select-actions">
            <v-btn
              color="error"
              outlined
              small
              :disabled="!form.selected.length"
              @click="handleClearAll"
            >
              Clear All
            </v-btn>
            <v-btn
              color="primary"
              outlined
              small
              :disabled="form.selected.length === allNumbers.length"
              @click="handleSelectAll"
            >
              Select All
            </v-btn>
          </div>
        </div>
        <div class="divider" />
        <toggle-buttons
          class="car-numbers"
          :items="carNumbersOptions"
          :value="form.selected"
          @change="handleOtherClick"
        />
      </v-card-text>
      <v-card-actions>
        <v-icon class="car-icon" size="18">$vuetify.icons.car</v-icon>
        <span><b>{{ form.selected.length }}</b> Car Numbers selected</span>
        <v-spacer />
        <v-btn
          color="primary"
          outlined
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :disabled="!form.selected.length"
          :loading="loading"
          @click="submit"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getRace, updateRace } from '../../api/races'
import AppLogo from '@/components/layout/AppLogo'
import BaseDatePicker from './BaseDatePicker'
import Loader from '@/components/Loader'
import ToggleButtons from '@/components/raceImageComponents/ToggleButtons'

export default {
  name: 'RaceSettingsDialog',
  components: {
    AppLogo,
    BaseDatePicker,
    Loader,
    ToggleButtons,
  },
  data() {
    return {
      allNumbers: Array.from(Array(100)).map((_, i) => i.toString()),
      form: {
        active : null,
        date: '',
        event : '',
        selected: [],
      },
      loading: false,
      race: {},
      visible: false,
    }
  },
  computed: {
    carNumbersOptions() {
      return this.allNumbers.map(el => ({
        label: el,
        value: el,
      }))
    },
    isRaceInfo() {
      return Object.keys(this.race).length
    },
  },
  methods: {
    close() {
      this.visible = false
    },
    getRaceInfo() {
      this.loading = true
      getRace(this.$route.params.raceId)
        .then(res => {
          this.race = res.data
          this.setForm(res.data)
        })
        .catch(e => console.error(e))
        .finally(() => this.loading = false)
    },
    handleClearAll() {
      this.form.selected = []
    },
    handleOtherClick(newSelected) {
      this.form.selected = newSelected
    },
    handleSelectAll() {
      this.form.selected = this.allNumbers
    },
    open() {
      this.visible = true
    },
    setForm({ race_active, race_cars, race_date, race_event }) {
      this.form.active = race_active
      this.form.date = new Date(race_date).toISOString().split('T')[0]
      this.form.event = race_event
      this.form.selected = race_cars.map(el => el.car_number) || []
    },
    submit() {
      const tempNumbers = this.race.race_cars.reduce((res, cur) => {
        res[cur.car_number] = cur
        return res
      }, {})
      const params = {
        race_active: this.form.active,
        race_date: new Date(this.form.date).toISOString(),
        race_event: this.form.event,
        race_cars: this.form.selected.map(number => {
          const car = tempNumbers[number]
          return car ? car : { car_number: number }
        }),
      }

      this.loading = true
      updateRace(this.race._id, params)
       .then(() => {
          this.getRaceInfo()
          this.close()
       })
      .finally(() => this.loading = false)
    }
  },
  watch: {
    visible(value) {
      if (value) {
        if (this.isRaceInfo) {
          this.setForm(this.race)
        } else {
          this.getRaceInfo()
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .custom-dialog {
    border-radius: 6px;
    box-shadow: var(--v-shadow-1);
  }

  .v-card__title {
    gap: 12px;
    padding: 6px 12px!important;
    background: var(--v-grey-lighten-5);
    box-shadow: inset 0px -1px 0px var(--v-grey-lighten-3);
    font-size: 16px!important;
    line-height: 24px;
  }

  .v-card__text {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 12px!important;
  }

  .v-card__actions {
    border-top: 1px solid var(--v-grey-lighten-3);
    background: var(--v-grey-lighten-5);
    font-size: 14px;
    padding: 12px!important;

    .v-btn {
      font-weight: 400;
      font-size: 16px;
      height: 32px;
      text-transform: capitalize;
    }
  }

  .select-actions {
    display: flex;
    gap: 8px;

    .v-btn {
      font-weight: 400;
      height: 24px;
      text-transform: capitalize;
    }
  }

  .car-numbers {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
    row-gap: 12px;
    column-gap: 7px;

    .v-btn {
      height: 30px;
      font-size: 14px;
    }
  }

  .divider {
    min-height: 1px;
    height: 1px;
    width: 100%;
    background: var(--v-grey-lighten-3);
  }

  .car-icon {
    margin-right: 10px;

    path {
      stroke: var(--v-secondary-base);
    }
  }

  .v-input--switch {
    margin-top: 0;

    .v-input--selection-controls__input {
      height: 16px;
      width: 32px;
    }

    .v-input--switch__track {
      height: inherit!important;
      width: inherit!important;
      left: 0!important;
      top: 0!important;
    }

    .v-input--switch__thumb {
      width: 12px;
      height: 12px;
      translate: 2px 4px;

      &.primary--text {
        translate: -2px 4px;
      }
    }

    label {
      color: var(--v-secondary-base);
    }
  }

  .v-text-field {
    fieldset {
      border-color: var(--v-primary-base);
    }

    input {
      color: var(--v-primary-base);
    }

    label {
      font-size: 12px;
      line-height: 16px;
      color: var(--v-secondary-base);
    }
  }
}
</style>
